import { useState } from 'react';
import { useNavigate } from 'react-router';
import { postJson } from '~/lib/http';
import { MicButton } from './mic-button';
import { filtersToHomeQuery, keywordHomeQuery, type SearchResponse } from '~/lib/search-nav';

/**
 * Natural-language event search. The AI parses the query into structured
 * filters (server-side); we reflect those filters into the home URL so results
 * are shareable and the filter box stays in sync. Always-works fallback: on any
 * error, the raw text becomes a keyword search on the calendar.
 */
export function AiSearch({ variant = 'light' }: { variant?: 'hero' | 'light' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(q: string) {
    const text = q.trim();
    if (!text) return;
    setPending(true);
    setError(null);
    const res = await postJson<SearchResponse>('/api/ai/search', { query: text });
    setPending(false);

    if (!res.ok) {
      // Never dead-end — fall back to a plain keyword search on the calendar.
      navigate(`/?${keywordHomeQuery(text)}`);
      return;
    }

    const qs = filtersToHomeQuery(
      res.data.filters,
      text,
      res.data.interpretation,
      Boolean(res.data.widened)
    );
    navigate(`/?${qs}`);
  }

  return (
    <div className={variant === 'hero' ? 'ai-search' : 'ai-search ai-search--light'}>
      <form
        className="ai-search-bar"
        onSubmit={(e) => {
          e.preventDefault();
          run(query);
        }}
      >
        <input
          className="ai-search-input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search anything"
          aria-label="Search events"
          disabled={pending}
        />
        <MicButton
          title="Search by voice"
          onResult={(text) => {
            setQuery(text);
            run(text);
          }}
        />
        <button type="submit" className="ui-btn btn-primary" disabled={pending}>
          {pending ? 'Searching…' : 'Search'}
        </button>
      </form>
      {error && <p className="form-error small">{error}</p>}
    </div>
  );
}
